import { getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table"
import type { ExpandedState, OnChangeFn, Row, SortingState } from "@tanstack/react-table"
import type { FC, Ref } from "react"
import { useState } from "react"

import { useTransactionsColumns } from "../hooks/useTransactionsColumns"

import { TransactionDetailsRow } from "./TransactionDetailsRow"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { GlTransactionRow } from "@/graphql/codegen/graphql"

interface TransactionsTableProps {
  data: GlTransactionRow[]
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  loading: boolean
  error?: string | null
  scrollHandler: () => void
  tableContainerRef?: Ref<HTMLDivElement>
  hasMaintenancePermission?: boolean
  onEdit?: (row: GlTransactionRow) => void
  onReverse?: (row: GlTransactionRow) => void
  onCancel?: (row: GlTransactionRow) => void
  onDocumentsOpen?: (row: GlTransactionRow) => void
}

export const TransactionsTable: FC<TransactionsTableProps> = ({
  data,
  sorting,
  onSortingChange,
  loading,
  error,
  scrollHandler,
  tableContainerRef,
  hasMaintenancePermission,
  onEdit,
  onReverse,
  onCancel,
  onDocumentsOpen,
}) => {
  const columns = useTransactionsColumns({ hasMaintenancePermission, onEdit, onReverse, onCancel, onDocumentsOpen })
  const [expandedState, setExpandedState] = useState<ExpandedState>({})

  const renderExpandedContent = (row: Row<GlTransactionRow>) => <TransactionDetailsRow row={row} />

  return (
    <AerosBaseTable<GlTransactionRow>
      error={error ? { message: error } : undefined}
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          state: {
            sorting,
            isLoading: loading,
            expanded: expandedState,
          },
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          getRowId: (row) => row.glTransactionRowId,
          onSortingChange,
          onExpandedChange: setExpandedState,
          getRowCanExpand: () => true,
          getExpandedRowModel: getExpandedRowModel(),
          renderExpandedContent,
        },
      }}
      slotProps={{
        container: {
          ref: tableContainerRef,
          onScroll: scrollHandler,
          className: "overflow-auto",
        },
        bodyRow: {
          className: "hover:bg-gray-50",
        },
        bodyCellExpanded: { className: "bg-primary-100" },
      }}
      noDataMessage='No transactions found'
    />
  )
}
